import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Heading3 } from '@myphx-engineering/phx-design-system';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import {
    CoverageCardType,
    CoverageStatus,
} from '../../../../graphql/generated/graphql';
import { useDrugName } from '../../../../hooks/use-drug-name';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';
import { AttestationContent } from '../common/AttestationContent';
import { ForwardToPharmacySelection } from '../common/ForwardToPharmacySelection';

export const InsuranceAttestation = () => {
    const { t } = useTranslation('lilly');

    const { coverage, drxId, medication } = useFillPrescription();
    const { originalPathway, setPathway } = useFillPathway();
    const terms = useProgramTerms(medication, 'insurance');
    const drugName = useDrugName(medication);

    const onDecline = useCallback(() => {
        setPathway({ pathway: 'self-pay' });
    }, [setPathway]);

    // No T&C means there isn't a copay assistance card, but there may still be a covered flow
    if (!terms) {
        return <ForwardToPharmacySelection drxId={drxId} />;
    }

    const showCoverageWarning =
        originalPathway !== 'fallback' && coverage === CoverageStatus.Unknown;

    return (
        <>
            <ConditionalComponent condition={showCoverageWarning}>
                <Heading3>
                    {t(
                        'fill.insurance.attestation.disclaimer.coverageUnknown',
                        { drugName }
                    )}
                </Heading3>
            </ConditionalComponent>
            <AttestationContent
                terms={terms}
                acceptLabel={t('fill.insurance.attestation.accept')}
                cardType={CoverageCardType.CopayAssistance}
                declineCallback={onDecline}
                declineLabel={t('fill.insurance.attestation.decline')}
            />
        </>
    );
};
